import { Component, Input, Output, EventEmitter, OnDestroy, OnInit, ChangeDetectionStrategy, ChangeDetectorRef } from '@angular/core';
import { Subject } from 'rxjs/Subject';
import 'rxjs/add/operator/takeUntil';

import { LearningFacadeService } from '../../../services/learning-facade.service';
import {
  SkillDocumentResponse,
  SkillPracticeTaskResponse,
  SkillPracticeTaskStatus,
} from '../../../models/skill-learning.model';

type SkillDocumentInput = SkillDocumentResponse | null;

@Component({
  selector: 'app-practice-level-container',
  templateUrl: './practice-level-container.component.html',
  styleUrls: ['./practice-level-container.component.css'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PracticeLevelContainerComponent implements OnInit, OnDestroy {
  @Input() specializationId: string;
  @Input() level: number;
  @Input() description: string;
  @Input() documentFileId: string | null = null;
  @Input() document: SkillDocumentInput = null;
  @Input() isDisabled: boolean = false;
  @Input() startHintKey: string = 'SKILL_LEARNING_PRACTICE_START_HINT';
  @Input() lockedMessageKey: string = 'SKILL_LEARNING_PRACTICE_LOCKED';

  @Output() completed = new EventEmitter<void>();

  taskStatus: SkillPracticeTaskStatus | null = null;
  curatorName: string | null = null;
  curatorPhone: string | null = null;
  planfixTaskId: number | null = null;
  planfixTaskUrl: string | null = null;
  isLoading: boolean = false;

  private destroy$ = new Subject<void>();
  private pollingInterval: any;

  constructor(
    private facade: LearningFacadeService,
    private cdr: ChangeDetectorRef,
  ) {}

  ngOnInit(): void {
    this.loadInitialTaskStatus();
  }

  ngOnDestroy(): void {
    this.clearPolling();
    this.destroy$.next();
    this.destroy$.complete();
  }

  get canStart(): boolean {
    return this.isRestartableStatus && !this.isDisabled;
  }

  get canShowStartButton(): boolean {
    return !this.isLoading && !this.isCompleted && this.isRestartableStatus;
  }

  get isStartButtonDisabled(): boolean {
    return !this.canStart;
  }

  get isCompleted(): boolean {
    return this.taskStatus === 'COMPLETED';
  }

  get canShowLockedMessage(): boolean {
    return this.isDisabled && !this.isCompleted && this.isRestartableStatus;
  }

  get normalizedDescription(): string {
    return (this.description || '').trim();
  }

  private get isRestartableStatus(): boolean {
    return !this.taskStatus || this.taskStatus === 'CANCELLED';
  }

  get documentUrl(): string | null {
    if (this.hasDocument) {
      return this.facade.buildDocumentDownloadUrl(this.specializationId, this.level);
    }
    return null;
  }

  get hasDocument(): boolean {
    return !!this.document || !!this.documentFileId;
  }

  loadTaskStatus(): void {
    this.isLoading = true;
    this.cdr.markForCheck();

    this.facade.getPracticeTaskStatus(this.specializationId, this.level)
      .takeUntil(this.destroy$)
      .subscribe(
        task => {
          this.applyTask(task);
          this.isLoading = false;
          this.cdr.markForCheck();

          if (task.status === 'CREATED' || task.status === 'IN_WORK') {
            this.startPolling();
          }
        },
        () => {
          this.resetTask();
          this.isLoading = false;
          this.cdr.markForCheck();
        },
      );
  }

  onStartClicked(): void {
    if (!this.canStart) {
      return;
    }

    this.isLoading = true;
    this.cdr.markForCheck();

    this.facade.startPracticeLevel(this.specializationId, this.level)
      .takeUntil(this.destroy$)
      .subscribe(
        task => {
          this.applyTask(task);
          this.isLoading = false;
          this.cdr.markForCheck();

          if (task.status === 'CREATED' || task.status === 'IN_WORK') {
            this.startPolling();
          }
        },
        () => {
          this.isLoading = false;
          this.cdr.markForCheck();
        },
      );
  }

  onDownloadClicked(): void {
    const url = this.documentUrl;
    if (url) {
      window.open(url, '_blank');
    }
  }

  private loadInitialTaskStatus(): void {
    if (!this.specializationId || !this.level) {
      return;
    }

    this.loadTaskStatus();
  }

  private startPolling(): void {
    this.clearPolling();
    this.pollingInterval = setInterval(() => {
      this.facade.getPracticeTaskStatus(this.specializationId, this.level)
        .takeUntil(this.destroy$)
        .subscribe(
          task => {
            this.applyTask(task);
            if (task.status === 'COMPLETED' || task.status === 'CANCELLED') {
              this.clearPolling();
              this.completed.emit();
            }
            this.cdr.markForCheck();
          },
        );
    }, 60000);
  }

  private clearPolling(): void {
    if (this.pollingInterval) {
      clearInterval(this.pollingInterval);
      this.pollingInterval = null;
    }
  }

  private applyTask(task: SkillPracticeTaskResponse): void {
    this.taskStatus = task.status;
    this.curatorName = this.resolveCuratorName(task);
    this.curatorPhone = this.resolveCuratorPhone(task);
    this.planfixTaskId = task.planfixTaskId;
    this.planfixTaskUrl = task.planfixTaskUrl;
  }

  private resetTask(): void {
    this.taskStatus = null;
    this.curatorName = null;
    this.curatorPhone = null;
    this.planfixTaskId = null;
    this.planfixTaskUrl = null;
  }

  private resolveCuratorName(task: SkillPracticeTaskResponse): string | null {
    if (task.assignedCurator && task.assignedCurator.fullName) {
      return task.assignedCurator.fullName;
    }
    return null;
  }

  private resolveCuratorPhone(task: SkillPracticeTaskResponse): string | null {
    if (task.assignedCurator && task.assignedCurator.phone) {
      return task.assignedCurator.phone;
    }
    return null;
  }
}



// WEBPACK FOOTER //
// C:/work/internal-frontend/src/app/content-page/common/skill-learning/components/user/practice-level/practice-level-container.component.ts